import { SITE_BASE_URL, BLOG_BASE_PATH } from '../config/siteConfig';

/**
 * Extract metadata from markdown frontmatter
 * Supports YAML-style frontmatter between --- delimiters
 */
export const extractMetadata = (markdownContent) => {
    const frontmatterRegex = /^---\s*\n([\s\S]*?)\n---\s*\n/;
    const match = markdownContent.match(frontmatterRegex);

    if (!match) {
        return null;
    }

    const frontmatter = match[1];
    const metadata = {};

    // Parse YAML-style key: value pairs
    const lines = frontmatter.split('\n');
    lines.forEach((line) => {
        const colonIndex = line.indexOf(':');
        if (colonIndex > 0) {
            const key = line.substring(0, colonIndex).trim();
            let value = line.substring(colonIndex + 1).trim();

            // Remove quotes if present
            if (
                (value.startsWith('"') && value.endsWith('"')) ||
                (value.startsWith("'") && value.endsWith("'"))
            ) {
                value = value.substring(1, value.length - 1);
            }

            metadata[key] = value;
        }
    });

    return metadata;
};

/**
 * Generate JSON-LD structured data for blog posts
 */
export const generateArticleSchema = (
    post,
    metadata,
    currentUrl,
    language = 'en'
) => {
    const fullUrl =
        currentUrl ||
        `${SITE_BASE_URL}${BLOG_BASE_PATH}/${language}/posts/${post.route}`;

    const schema = {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: metadata?.title || post.title,
        description: metadata?.metaDescription || post.description,
        image: post.image,
        author: {
            '@type': metadata?.authorType || 'Organization',
            name: metadata?.author || 'Dizen',
            url: metadata?.authorUrl || SITE_BASE_URL,
        },
        publisher: {
            '@type': 'Organization',
            name: 'Dizen',
            logo: {
                '@type': 'ImageObject',
                url: `${SITE_BASE_URL}/icon.png`,
            },
        },
        datePublished: metadata?.datePublished || post.date,
        dateModified:
            metadata?.dateModified || metadata?.datePublished || post.date,
        mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': fullUrl,
        },
    };

    return schema;
};

/**
 * Generate FAQ Schema separately
 */
export const generateFAQSchema = (metadata) => {
    if (!metadata?.faq) {
        return null;
    }

    try {
        const faqData = JSON.parse(metadata.faq);
        return {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqData.map((item) => ({
                '@type': 'Question',
                name: item.question,
                acceptedAnswer: {
                    '@type': 'Answer',
                    text: item.answer,
                },
            })),
        };
    } catch (e) {
        console.error('Error parsing FAQ data:', e);
        return null;
    }
};

/**
 * Get canonical URL for a post
 */
export const getCanonicalUrl = (route, language = 'en') => {
    return `${SITE_BASE_URL}${BLOG_BASE_PATH}/${language}/posts/${route}`;
};

/**
 * Generate full image URL
 */
export const getFullImageUrl = (imageUrl) => {
    if (imageUrl.startsWith('http')) {
        return imageUrl;
    }
    return `${SITE_BASE_URL}${imageUrl}`;
};
